import { ATTRIBUTE_MAX, ATTRIBUTE_MIN, CHARACTER_ATTRIBUTE_DESCRIPTIONS, type CharacterAttributes as CharacterAttributesData } from '@sorrem/shared';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Button, Icon, Separator } from '@sorrem/ui';
import { StatRow } from '../StatRow';
import { InfoIcon } from '../InfoIcon';
import styles from './CharacterAttributes.module.scss';

/**
 * Props for the character attributes panel.
 */
interface CharacterAttributesProps {
    attributes: Partial<CharacterAttributesData>;
    mode?: 'read' | 'edit';
    remainingPoints?: number;
    minValue?: number;
    maxValue?: number;
    isDisabled?: boolean;
    /** Whether to show the explanatory note under the title. Defaults to true. */
    isNoteShown?: boolean;
    /** Control at the right end of the title row, e.g. the Reshape action. */
    headerAction?: ReactNode;
    onChangeAttribute?: (attribute: keyof CharacterAttributesData, nextValue: number) => void;
    className?: string;
}

const ATTRIBUTE_DEFINITIONS: Array<{ key: keyof CharacterAttributesData; label: string; description: string }> = [
    { key: 'vitality', label: 'Vitality', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.vitality },
    { key: 'strength', label: 'Strength', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.strength },
    { key: 'agility', label: 'Agility', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.agility },
    { key: 'quickness', label: 'Quickness', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.quickness },
    { key: 'intellect', label: 'Intellect', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.intellect },
    { key: 'endurance', label: 'Endurance', description: CHARACTER_ATTRIBUTE_DESCRIPTIONS.endurance },
];

export function CharacterAttributes({
    attributes,
    mode = 'read',
    remainingPoints,
    minValue = ATTRIBUTE_MIN,
    maxValue = ATTRIBUTE_MAX,
    isDisabled = false,
    isNoteShown = true,
    headerAction,
    onChangeAttribute,
    className,
}: CharacterAttributesProps): JSX.Element {
    return (
        <div className={classNames(styles.characterAttributes, className)}>
            <div className={classNames(styles.sectionTitle)}>
                <div className={styles.sectionTitleMain}>
                    <h4>Attributes</h4>
                    <InfoIcon
                        ariaLabel="Show attribute descriptions"
                        className={styles.infoIconAnchor}
                        popoverPlacement="top"
                        popoverContent={(
                            <div className={styles.infoPopoverContent}>
                                {ATTRIBUTE_DEFINITIONS.map((detail) => (
                                    <div key={detail.label} className={styles.infoPopoverLine}>
                                        <span className={styles.infoPopoverLabel}>{detail.label}:</span> {detail.description}
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                </div>
                {headerAction !== undefined && <div className={styles.sectionTitleAction}>{headerAction}</div>}
            </div>
            {isNoteShown && (
                <p className={styles.sectionNote}>Attributes are base values that influence growth, survivability, and combat potential.</p>
            )}
            <dl className={classNames(styles.attributesList, mode === 'edit' && styles.attributesListEdit)}>
                {ATTRIBUTE_DEFINITIONS.map((definition) => {
                    const value = attributes[definition.key] ?? 0;
                    if (mode === 'read') {
                        return <StatRow key={definition.key} label={definition.label} value={value} />;
                    }

                    const canDecrease = value > minValue;
                    const canIncrease = value < maxValue && (remainingPoints ?? 0) > 0;

                    return (
                        <div key={definition.key} className={styles.attributeEditRow}>
                            <span className={styles.attributeLabel}>{definition.label}</span>
                            <Separator className={styles.attributeFiller} />

                            <div className={styles.attributeEditControls}>
                                <Button
                                    shape="round"
                                    className={classNames(styles.adjustButton, !canDecrease && styles.disabled)}
                                    onClick={() => onChangeAttribute?.(definition.key, value - 1)}
                                    disabled={!canDecrease || isDisabled}
                                    aria-label={`Decrease ${definition.label}`}
                                >
                                    <Icon icon="minus" size="small" tooltip={`Decrease ${definition.label}`} />
                                </Button>

                                <span className={styles.attributeValue}>{value}</span>

                                <Button
                                    shape="round"
                                    className={classNames(styles.adjustButton, !canIncrease && styles.disabled)}
                                    onClick={() => onChangeAttribute?.(definition.key, value + 1)}
                                    disabled={!canIncrease || isDisabled}
                                    aria-label={`Increase ${definition.label}`}
                                >
                                    <Icon icon="plus" size="small" tooltip={`Increase ${definition.label}`} />
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </dl>
            {mode === 'edit' && typeof remainingPoints === 'number' && (
                <div className={styles.pointsRemaining}>{remainingPoints} points remaining</div>
            )}

        </div>
    );
}
